import { contarErrorSeguimiento, desenlaceSeguimiento } from 'api/nominas/seguimientoProceso';

// Una consulta de progreso cada 2 s
export const INTERVALO_SEGUIMIENTO_MS = 2000;

/**
 * Da seguimiento periódico a un proceso de timbrado en background.
 * - El tope cuenta intervalos, no respuestas, para que el tiempo máximo no crezca
 *   si una consulta tarda; nunca se lanza una consulta con otra en vuelo.
 * - El tope también vence por tiempo transcurrido (maxConsultas × intervaloMs desde el
 *   inicio): el navegador espacia los intervalos de una pestaña en segundo plano
 *   (hasta 1 por minuto) y, contando solo intervalos, el aviso de inicio no confirmado
 *   podía no salir nunca (ticket 1, criterio 4).
 * - Una respuesta que llega después de detener se ignora.
 * - Una consulta fallida no trae avance real: no se reporta avance.
 * @param {object} params
 * @param {(opciones: {seguimiento: object|null, vencido: boolean}) => Promise<object>}
 *   params.consultar consulta de progreso (getProgresoProceso); no debe lanzar
 * @param {number} params.maxConsultas intervalos antes de dejar de dar seguimiento
 * @param {({enCurso: number, total: number}) => void} [params.onAvance]
 * @param {() => void} [params.onInicioConfirmado] la primera vez que se ve en curso
 * @param {(desenlace: string, progreso: object) => void} params.onFin al detenerse solo
 * @param {number} [params.intervaloMs]
 * @param {Function} [params.setIntervalFn] inyectable para pruebas
 * @param {Function} [params.clearIntervalFn] inyectable para pruebas
 * @param {() => number} [params.ahoraFn] reloj en ms, inyectable para pruebas
 * @returns {() => void} detiene el seguimiento (sin llamar onFin)
 */
export const seguirProceso = ({
  consultar,
  maxConsultas,
  onAvance = () => {},
  onInicioConfirmado = () => {},
  onFin,
  intervaloMs = INTERVALO_SEGUIMIENTO_MS,
  setIntervalFn = setInterval,
  clearIntervalFn = clearInterval,
  ahoraFn = Date.now,
}) => {
  const inicio = ahoraFn();
  const tiempoMaximoMs = maxConsultas * intervaloMs;
  let intentos = 0;
  let seguimiento = null;
  let erroresConsecutivos = 0;
  let consultaEnVuelo = false;
  let detenido = false;
  let intervalo = null;

  const detener = () => {
    detenido = true;
    clearIntervalFn(intervalo);
  };

  intervalo = setIntervalFn(async () => {
    intentos += 1;
    if (consultaEnVuelo || detenido) return;
    consultaEnVuelo = true;
    try {
      const vencido = intentos >= maxConsultas || ahoraFn() - inicio >= tiempoMaximoMs;
      const progreso = await consultar({ seguimiento, vencido });
      if (detenido) return;

      const cuentaErrores = contarErrorSeguimiento(erroresConsecutivos, progreso.error);
      erroresConsecutivos = cuentaErrores.consecutivos;
      // Lo ya confirmado (proceso visto en curso) no se pierde con respuestas posteriores
      const yaVisto = !!seguimiento?.vistoEnCurso;
      if (!yaVisto) seguimiento = progreso.seguimiento;
      if (!yaVisto && seguimiento?.vistoEnCurso) onInicioConfirmado();

      if (!progreso.error) onAvance({ enCurso: progreso.enCurso, total: progreso.total });

      if (progreso.terminado || vencido || cuentaErrores.detener) {
        detener();
        const desenlace = desenlaceSeguimiento({
          progreso, detenidoPorErrores: cuentaErrores.detener,
        });
        onFin(desenlace, progreso);
      }
    } catch (error) {
      // consultar no lanza; un error aquí se reintenta en el siguiente intervalo
    } finally {
      consultaEnVuelo = false;
    }
  }, intervaloMs);

  return detener;
};
